import { z } from 'zod';
import type { Tool } from './Tool';
import type { Skill } from '@/contexts/SkillsContext';

export interface SkillToolDeps {
  getSkillByName: (name: string) => Skill | undefined;
  getEnabledSkills: () => Skill[];
}

const inputSchema = z.object({
  name: z.string().describe('The name of the skill to execute (lowercase alphanumeric with hyphens)'),
});

type SkillToolInput = z.infer<typeof inputSchema>;

export function createSkillTool(deps: SkillToolDeps): Tool<SkillToolInput> {
  return {
    description: 'Load and execute a skill from the configured plugins. Skills are reusable AI workflows that can be invoked by name.',
    inputSchema,
    async execute(args: SkillToolInput): Promise<string> {
      const { name } = args;

      const skill = deps.getSkillByName(name);
      
      if (!skill) {
        const availableSkills = deps.getEnabledSkills();
        if (availableSkills.length === 0) {
          return `Error: No skills are currently configured. Users can add skills in Settings > AI by adding plugins that contain skills.`;
        }
        
        const skillNames = availableSkills.map(s => `- ${s.name}: ${s.description}`).join('\n');
        return `Error: Skill "${name}" not found.\n\nAvailable skills:\n${skillNames}`;
      }

      // Return the skill's instructions for the AI to follow
      return `## Skill: ${skill.name}

**Description**: ${skill.description}
${skill.license ? `**License**: ${skill.license}` : ''}

### Instructions

${skill.content}

---
*Execute the above instructions to complete the skill.*`;
    },
  };
}
